/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1740096000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Feb 21 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "sharepoint",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Microsoft Sharepoint",
          "depth": 0,
          "id": "connect-to-microsoft-sharepoint"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Select file format",
          "depth": 1,
          "id": "select-file-format"
        }, {
          "value": "Select a Site and Drive",
          "depth": 1,
          "id": "select-a-site-and-drive"
        }, {
          "value": "Enter filename",
          "depth": 1,
          "id": "enter-filename"
        }, {
          "value": "Set filename offset",
          "depth": 1,
          "id": "set-filename-offset"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      a: "a",
      h3: "h3",
      code: "code",
      ul: "ul",
      li: "li",
      em: "em",
      h4: "h4"
    }, _provideComponents(), props.components), {RowAndColumnOrderingInfo, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!RowAndColumnOrderingInfo) _missingMdxReference("RowAndColumnOrderingInfo", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Any data set"
              })
            }), _jsx(_components.td, {
              children: "Sync data from a source to SharePoint as JSON or CSV files"
            }), _jsx(_components.td, {
              children: "Insert, All, Diff"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(RowAndColumnOrderingInfo, {
        variant: "file"
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-microsoft-sharepoint",
        children: "Connect to Microsoft Sharepoint"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Microsoft Sharepoint"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Microsoft Sharepoint"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["For the ", _jsx(_components.strong, {
          children: "Authentication method"
        }), ", select ", _jsx(_components.strong, {
          children: "Log in to Sharepoint"
        }), " and log into your Microsoft Sharepoint account. Once successful, you will be redirected back to Hightouch to enter a descriptive name for your destination and complete setup."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Microsoft Sharepoint destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Microsoft Sharepoint destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "select-file-format",
        children: "Select file format"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing JSON and CSV files to SharePoint."
      }), "\n", _jsx(_components.h3, {
        id: "select-a-site-and-drive",
        children: "Select a Site and Drive"
      }), "\n", _jsx(_components.p, {
        children: "In order to sync to SharePoint, you must select a site and a drive available to your account.\nYou can specify a folder within the site/drive combination, or create a new folder to write the file to."
      }), "\n", _jsx(_components.h3, {
        id: "enter-filename",
        children: "Enter filename"
      }), "\n", _jsxs(_components.p, {
        children: ["The filename field lets you specify the parent directory and the name of the file you want to use for your results.\nYou can include timestamp variables in the filename, surrounding each with ", _jsx(_components.code, {
          children: "{}"
        }), ".\nHightouch supports these timestamp variables:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "YYYY"
          }), ": Represents the full year in four digits."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "YY"
          }), ": The last two digits of the year."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "MM"
          }), ": Two-digit month format (01-12)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "DD"
          }), ": Two-digit day format (01-31)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "HH"
          }), ": Two-digit hour format in 24-hour clock (00-23)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "mm"
          }), ": Two-digit minute format (00-59)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "ss"
          }), ": Two-digit second format (00-59)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "ms"
          }), ": Three-digit millisecond format."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "X"
          }), ": Unix timestamp in seconds."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "x"
          }), ": Unix timestamp in milliseconds."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "All dates and times are UTC."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, you could enter ", _jsx(_components.code, {
          children: "upload/{YYYY}-{MM}-{DD}-{HH}-{mm}-result.json"
        }), " to dynamically include the year, month, date, hour, and minute in each uploaded file. Hightouch would insert each file in the ", _jsx(_components.code, {
          children: "upload"
        }), " directory, which would need to already exist in your bucket."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can also use other ", _jsx(_components.a, {
          href: "/syncs/mapping-data#variable-values",
          children: "variable values"
        }), " to include sync metadata in the filename:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{model.id}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{model.name}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{sync.id}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{sync.run.id}"
          })
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "If a file already exists at the path you entered at the time of a sync, Hightouch will overwrite the file."
        })
      }), "\n", _jsx(_components.h3, {
        id: "set-filename-offset",
        children: "Set filename offset"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch uses the UTC timestamp of the sync run to fill in timestamp variables. You can optionally include an offset in seconds. For example, if you want the filename's date to be 24 hours ", _jsx(_components.em, {
          children: "before"
        }), " the sync takes place, enter '-86400' (", _jsx(_components.code, {
          children: "24 hours * 60 minutes * 60 seconds"
        }), ")."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows from your model to any data set in Microsoft Sharepoint on any column in your model and any field in Microsoft Sharepoint. Ensure the data types of the model column and Microsoft Sharepoint field you select match.\nRefer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync any data set fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to the default any data set fields.\nEnsure your model's columns have the same data types as the fields you want to sync to."]
      }), "\n", _jsx(_components.h4, {
        id: "empty-file-results",
        children: "Empty file results"
      }), "\n", _jsxs(_components.p, {
        children: ["You can select how Hightouch should handle empty results files. Empty result files can occur if your model's query results haven't changed since the last sync for Insert and Diff modes.\nYou can select whether to ", _jsx(_components.strong, {
          children: "skip empty files"
        }), ". If you skip empty files, it means Hightouch won't export any files if your model's query results haven't changed since your last sync."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
